import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { apiFetch } from '@/lib/api'
import { DateTimePicker } from '@/components/ui/DateTimePicker'
import { MissionChat } from '@/components/ui/MissionChat'
import { useAuth } from '@/lib/hooks/useAuth'
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete'
import {
  formatEur,
  formatDate,
  formatDuration,
  MISSION_STATUS_LABEL,
  MISSION_STATUS_COLOR,
  MISSION_TYPE_LABEL,
} from '@/lib/format'

interface Review {
  id: string
  rating: number
  punctuality: number
  behavior: number
  comment: string | null
  clientFeedback: string | null
  createdAt: string
}

function StarRating({
  value,
  onChange,
  readonly = false,
}: {
  value: number
  onChange?: (v: number) => void
  readonly?: boolean
}) {
  const [hovered, setHovered] = useState(0)
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => onChange?.(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(0)}
          className={`text-2xl transition-transform ${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'}`}
          aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
        >
          <span className={(hovered || value) >= star ? 'text-warning' : 'text-white/20'}>★</span>
        </button>
      ))}
    </div>
  )
}

interface Assignment {
  id: string
  status: string
  acceptedAt: string | null
  provider: {
    id: string
    firstName: string
    lastName: string
    rating: number
    avatarUrl: string | null
    stripeAccountId: string | null
    stripeAccountEnabled: boolean
    paypalEmail: string | null
    priorityScore: number
    priorityTier: string
  }
}

interface Payment {
  id: string
  status: string
  amount: number
  platformFee: number
  providerNet: number
  method: string
  stripeCheckoutId: string | null
  stripeIntentId: string | null
  capturedAt: string | null
}

interface MissionDetail {
  id: string
  notifiedCount: number
  title: string
  description: string
  type: string
  status: string
  address: string
  city: string
  postalCode: string
  date: string
  duration: number
  amount: number
  radiusKm: number
  deadline: string | null
  minParticipants: number | null
  requiredSpecialty: string | null
  targetProviderId: string | null
  targetedUntil: string | null
  targetProvider: { id: string; firstName: string; lastName: string; avatarUrl: string | null } | null
  requiredSpecialties: { specialty: string; quantity: number }[] | null
  createdAt: string
  assignments: Assignment[]
  _count: { assignments: number }
  payment: Payment | null
  review: Review | null
}

function InfoItem({ icon, label, value }: { icon: string; label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xl flex-shrink-0 mt-0.5">{icon}</span>
      <div>
        <p className="text-text-muted text-xs mb-0.5">{label}</p>
        <p className="text-text-primary text-sm font-medium">{value}</p>
      </div>
    </div>
  )
}

const TIER_CONFIG = {
  PREMIUM: { label: 'Top prestataire', bg: 'bg-[#F59E0B]/15', border: 'border-[#F59E0B]/35', text: 'text-[#F59E0B]', icon: '⭐' },
  ACTIVE:  { label: 'Actif',           bg: 'bg-accent/10',    border: 'border-accent/25',    text: 'text-accent',       icon: '✦' },
  STANDARD: null,
}

function ProviderCard({ assignment }: { assignment: Assignment }) {
  const p = assignment.provider
  const tier = TIER_CONFIG[p.priorityTier as keyof typeof TIER_CONFIG] ?? null
  return (
    <div className="glass-card p-4 flex items-center gap-4">
      <div className="w-10 h-10 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center flex-shrink-0">
        <span className="text-accent text-sm font-bold">
          {p.firstName[0]}{p.lastName[0]}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-text-primary text-sm font-medium">
            {p.firstName} {p.lastName}
          </p>
          {tier && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${tier.bg} ${tier.border} ${tier.text}`}>
              {tier.icon} {tier.label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          {p.rating > 0 ? (
            <>
              <span className="text-warning text-xs">★</span>
              <span className="text-text-secondary text-xs">{p.rating.toFixed(1)}</span>
              {p.priorityScore > 0 && (
                <span className="text-text-muted text-xs ml-1">· Score {Math.round(p.priorityScore)}/100</span>
              )}
            </>
          ) : (
            <span className="text-text-muted text-xs">Pas encore d'avis</span>
          )}
        </div>
      </div>
      <span
        className={`text-xs px-2 py-0.5 rounded-full border ${
          assignment.status === 'ACCEPTED'
            ? 'bg-success/10 border-success/25 text-success'
            : assignment.status === 'PENDING'
            ? 'bg-warning/10 border-warning/25 text-warning'
            : 'bg-white/[0.06] border-white/[0.12] text-text-muted'
        }`}
      >
        {assignment.status === 'ACCEPTED' ? 'Accepté' : assignment.status === 'PENDING' ? 'En attente' : 'Décliné'}
      </span>
    </div>
  )
}

const PAYMENT_STATUS: Record<string, { label: string; color: string }> = {
  PENDING:    { label: 'En attente',  color: 'bg-white/[0.06] border-white/[0.12] text-text-muted' },
  AUTHORIZED: { label: 'Autorisé',   color: 'bg-warning/10 border-warning/25 text-warning' },
  CAPTURED:   { label: 'Capturé',    color: 'bg-success/10 border-success/25 text-success' },
  FAILED:     { label: 'Échoué',     color: 'bg-danger/10 border-danger/25 text-danger' },
}

export default function MissionDetailPage() {
  const params = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const missionId = params.id as string
  const paymentResult = searchParams.get('payment')
  const paypalReturn = searchParams.get('paypal')
  useAuth('COMPANY')

  const [mission, setMission] = useState<MissionDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [cancelling, setCancelling] = useState(false)
  const [editing, setEditing] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')
  const [editMsg, setEditMsg] = useState('')
  const [selectedSpecialties, setSelectedSpecialties] = useState<Record<string, number>>({})
  const [isCnpas, setIsCnpas] = useState(false)
  const [specialtyConfigs, setSpecialtyConfigs] = useState<{ specialty: string; label: string; shortLabel: string; icon: string; description: string; category: string; active: boolean }[]>([])

  useEffect(() => {
    apiFetch<{ isCnpas: boolean }>('/companies/me')
      .then((d) => setIsCnpas(d.isCnpas ?? false))
      .catch(() => {})
    apiFetch<{ specialty: string; label: string; shortLabel: string; icon: string; description: string; category: string; active: boolean }[]>('/public/specialty-config')
      .then(setSpecialtyConfigs)
      .catch(() => {})
  }, [])
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [paypalLoading, setPaypalLoading] = useState(false)
  const [cgvAccepted, setCgvAccepted] = useState(false)
  const [authorizing, setAuthorizing] = useState(false)
  const [completeLoading, setCompleteLoading] = useState(false)
  const [actionError, setActionError] = useState('')
  const [reviewPunctuality, setReviewPunctuality] = useState(0)
  const [reviewBehavior, setReviewBehavior] = useState(0)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewClientFeedback, setReviewClientFeedback] = useState('')
  const [reviewLoading, setReviewLoading] = useState(false)
  const [reviewError, setReviewError] = useState('')

  useEffect(() => {
    apiFetch<MissionDetail>(`/missions/${missionId}`)
      .then(setMission)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }, [missionId])

  // Auto-autoriser après retour PayPal (?paypal=return)
  useEffect(() => {
    if (paypalReturn === 'return' && mission?.status === 'ASSIGNED' && mission.payment?.status === 'PENDING') {
      authorizePayPal()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mission?.id, paypalReturn])

  useEffect(() => {
    if (editing && mission?.requiredSpecialties) {
      const map: Record<string, number> = {}
      for (const s of mission.requiredSpecialties) map[s.specialty] = s.quantity
      setSelectedSpecialties(map)
    } else if (!editing) {
      setSelectedSpecialties({})
    }
  }, [editing])

  function toggleSpecialty(value: string) {
    setSelectedSpecialties((prev) => {
      if (prev[value]) { const next = { ...prev }; delete next[value]; return next }
      return { ...prev, [value]: 1 }
    })
  }

  function setQuantity(value: string, qty: number) {
    setSelectedSpecialties((prev) => ({ ...prev, [value]: Math.max(1, qty) }))
  }

  async function authorizePayPal() {
    setAuthorizing(true)
    setActionError('')
    try {
      await apiFetch(`/payments/paypal/missions/${missionId}/authorize`, { method: 'POST' })
      const updated = await apiFetch<MissionDetail>(`/missions/${missionId}`)
      setMission(updated)
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Erreur PayPal')
    } finally {
      setAuthorizing(false)
    }
  }

  async function handlePay() {
    setPaymentLoading(true)
    setActionError('')
    try {
      const { url } = await apiFetch<{ url: string }>(
        `/payments/stripe/missions/${missionId}/checkout`,
        { method: 'POST' },
      )
      window.location.href = url
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Erreur paiement')
      setPaymentLoading(false)
    }
  }

  async function handlePayPal() {
    setPaypalLoading(true)
    setActionError('')
    try {
      const { approvalUrl } = await apiFetch<{ approvalUrl: string }>(
        `/payments/paypal/missions/${missionId}/order`,
        { method: 'POST' },
      )
      window.location.href = approvalUrl
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Erreur PayPal')
      setPaypalLoading(false)
    }
  }

  async function handleCompleteWithPayment() {
    if (!confirm('Confirmer la fin de la mission ? Le paiement sera libéré au prestataire.')) return
    setCompleteLoading(true)
    setActionError('')
    try {
      const endpoint =
        mission?.payment?.method === 'PAYPAL'
          ? `/payments/paypal/missions/${missionId}/complete`
          : `/payments/stripe/missions/${missionId}/complete`
      await apiFetch(endpoint, { method: 'POST' })
      const updated = await apiFetch<MissionDetail>(`/missions/${missionId}`)
      setMission(updated)
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setCompleteLoading(false)
    }
  }

  async function handleCompleteNoPayment() {
    if (!confirm('Confirmer la fin de la mission ? Elle passera en statut Terminée.')) return
    setCompleteLoading(true)
    setActionError('')
    try {
      await apiFetch(`/missions/${missionId}/complete`, { method: 'POST' })
      const updated = await apiFetch<MissionDetail>(`/missions/${missionId}`)
      setMission(updated)
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : 'Erreur')
    } finally {
      setCompleteLoading(false)
    }
  }

  async function handleReview(e: React.FormEvent) {
    e.preventDefault()
    if (reviewPunctuality === 0 || reviewBehavior === 0) {
      setReviewError('Évaluez la ponctualité et le comportement')
      return
    }
    setReviewLoading(true)
    setReviewError('')
    try {
      await apiFetch(`/reviews/missions/${missionId}`, {
        method: 'POST',
        body: JSON.stringify({
          punctuality: reviewPunctuality,
          behavior: reviewBehavior,
          comment: reviewComment || undefined,
          clientFeedback: reviewClientFeedback || undefined,
        }),
      })
      const updated = await apiFetch<MissionDetail>(`/missions/${missionId}`)
      setMission(updated)
    } catch (e: unknown) {
      setReviewError(e instanceof Error ? e.message : 'Erreur lors de l\'envoi')
    } finally {
      setReviewLoading(false)
    }
  }

  async function handleEdit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setEditSaving(true); setEditError(''); setEditMsg('')
    const fd = new FormData(e.currentTarget)
    const isAssigned = mission?.status === 'ASSIGNED'
    if (isAssigned && !confirm('La mission a déjà été acceptée. La modifier remettra le prestataire en attente de re-confirmation. Continuer ?')) {
      setEditSaving(false); return
    }
    try {
      const body: Record<string, unknown> = {
        title: fd.get('title'),
        description: fd.get('description'),
        type: fd.get('type'),
        address: fd.get('address'),
        city: fd.get('city'),
        postalCode: fd.get('postalCode'),
        date: fd.get('date') as string,
        duration: Number(fd.get('duration')),
        amount: Number(fd.get('amount')),
        radiusKm: Number(fd.get('radiusKm')),
        minParticipants: fd.get('minParticipants') ? Number(fd.get('minParticipants')) : null,
        requiredSpecialties: Object.keys(selectedSpecialties).length > 0
          ? Object.entries(selectedSpecialties).map(([specialty, quantity]) => ({ specialty, quantity }))
          : [],
      }
      const deadline = fd.get('deadline') as string
      if (deadline) body.deadline = deadline
      await apiFetch(`/missions/${missionId}`, { method: 'PATCH', body: JSON.stringify(body) })
      const updated = await apiFetch<MissionDetail>(`/missions/${missionId}`)
      setMission(updated)
      setEditing(false)
      setEditMsg(isAssigned ? '✅ Mission modifiée — le prestataire doit re-confirmer' : '✅ Mission modifiée')
    } catch (err: unknown) { setEditError(err instanceof Error ? err.message : 'Erreur') }
    finally { setEditSaving(false) }
  }

  async function cancelMission() {
    if (!confirm('Annuler cette mission ? Cette action est irréversible.')) return
    setCancelling(true)
    try {
      await apiFetch(`/missions/${missionId}`, { method: 'DELETE' })
      navigate('/missions')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur lors de l\'annulation')
      setCancelling(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 glass-card animate-pulse" />
        <div className="h-48 glass-card animate-pulse" />
        <div className="h-48 glass-card animate-pulse" />
      </div>
    )
  }

  if (error || !mission) {
    return (
      <div className="glass-card p-12 text-center">
        <p className="text-4xl mb-4">⚠️</p>
        <p className="text-text-primary font-medium mb-2">{error || 'Mission introuvable'}</p>
        <Link to="/missions" className="glass-btn px-5 py-2.5 text-sm rounded-xl inline-block mt-4">
          ← Retour aux missions
        </Link>
      </div>
    )
  }

  const canCancel = mission.status === 'OPEN'
  const canEdit = mission.status === 'OPEN' || mission.status === 'ASSIGNED'
  const acceptedAssignments = mission.assignments
    .filter((a) => a.status === 'ACCEPTED')
    .sort((a, b) => (b.provider.priorityScore ?? 0) - (a.provider.priorityScore ?? 0))
  const pendingAssignments = mission.assignments
    .filter((a) => a.status === 'PENDING')
    .sort((a, b) => (b.provider.priorityScore ?? 0) - (a.provider.priorityScore ?? 0))
  const assignedProvider = acceptedAssignments[0]?.provider
  const hasActivePayment = !!mission.payment && !['FAILED', 'REFUNDED'].includes(mission.payment.status)
  const canPayStripe = mission.status === 'ASSIGNED' && !hasActivePayment && !!assignedProvider?.stripeAccountEnabled
  const canPayPayPal = mission.status === 'ASSIGNED' && !hasActivePayment && !!assignedProvider?.paypalEmail
  const canCompleteWithPayment = mission.status === 'ASSIGNED' && mission.payment?.status === 'AUTHORIZED'
  const canCompleteNoPayment = mission.status === 'ASSIGNED' && !mission.payment

  return (
    <div>
      {/* Header */}
      <div className="flex items-start gap-4 mb-8">
        <Link to="/missions" className="text-text-muted hover:text-text-secondary transition-colors text-sm mt-1 flex-shrink-0">
          ←
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <span
              className={`text-xs px-2.5 py-1 rounded-full border ${MISSION_STATUS_COLOR[mission.status]}`}
            >
              {MISSION_STATUS_LABEL[mission.status]}
            </span>
            <span className="text-xs px-2.5 py-1 rounded-full border bg-white/[0.06] border-white/[0.12] text-text-secondary">
              {MISSION_TYPE_LABEL[mission.type] ?? mission.type}
            </span>
            {mission.targetProvider && (
              <span
                className={`text-xs px-2.5 py-1 rounded-full border ${
                  assignedProvider?.id === mission.targetProvider.id
                    ? 'bg-success/10 border-success/25 text-success'
                    : mission.status === 'OPEN' && mission.targetedUntil && new Date(mission.targetedUntil) > new Date()
                      ? 'bg-warning/10 border-warning/25 text-warning'
                      : 'bg-white/[0.06] border-white/[0.12] text-text-secondary'
                }`}
                title="Mission proposée à un expert choisi sur « Trouver un expert »"
              >
                ⭐ Proposée à {mission.targetProvider.firstName} {mission.targetProvider.lastName?.[0]?.toUpperCase()}.
                {assignedProvider?.id === mission.targetProvider.id
                  ? ' — a accepté'
                  : mission.status === 'OPEN' && mission.targetedUntil && new Date(mission.targetedUntil) > new Date()
                    ? ' — en attente de sa réponse (exclusivité 15 min)'
                    : mission.status === 'OPEN' ? ' — sans réponse, diffusée aux autres' : ''}
              </span>
            )}
          </div>
          <h1 className="text-2xl font-bold text-text-primary">{mission.title}</h1>
          <p className="text-text-muted text-xs mt-1">Publiée le {formatDate(mission.createdAt)}</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {canEdit && !editing && (
            <button
              onClick={() => { setEditing(true); setEditError(''); setEditMsg('') }}
              className="glass-btn px-4 py-2 text-xs rounded-xl"
            >
              ✏️ Modifier
            </button>
          )}
          {canCancel && (
            <button
              onClick={cancelMission}
              disabled={cancelling}
              className="glass-btn px-4 py-2 text-xs rounded-xl text-danger border-danger/20 hover:bg-danger/10"
            >
              {cancelling ? 'Annulation…' : 'Annuler la mission'}
            </button>
          )}
          {['ASSIGNED', 'IN_PROGRESS', 'COMPLETED'].includes(mission.status) && (
            <MissionChat missionId={mission.id} myRole="COMPANY" />
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 px-4 py-3 rounded-xl bg-danger/10 border border-danger/25 text-danger text-sm">
          {error}
        </div>
      )}

      {/* Banners retour paiement */}
      {paymentResult === 'success' && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-success/10 border border-success/25 text-success text-sm">
          ✅ Paiement autorisé ! Confirmez la fin de mission pour libérer les fonds au prestataire.
        </div>
      )}
      {paymentResult === 'cancelled' && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-warning/10 border border-warning/25 text-warning text-sm">
          ⚠️ Paiement annulé. Vous pouvez réessayer quand vous le souhaitez.
        </div>
      )}
      {authorizing && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-accent/10 border border-accent/25 text-[#8B9AFF] text-sm flex items-center gap-2">
          <span className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin flex-shrink-0" />
          Autorisation du paiement PayPal en cours…
        </div>
      )}
      {paypalReturn === 'return' && !authorizing && mission?.payment?.status === 'AUTHORIZED' && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-success/10 border border-success/25 text-success text-sm">
          ✅ Paiement PayPal autorisé ! Confirmez la fin de mission pour libérer les fonds.
        </div>
      )}

      {editMsg && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-success/10 border border-success/25 text-success text-sm">
          {editMsg}
        </div>
      )}

      {/* Formulaire d'édition */}
      {editing && (
        <div className="glass-card p-6 mb-4">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Modifier la mission</h2>

          {mission.status === 'ASSIGNED' && (
            <div className="mb-4 px-4 py-3 rounded-xl bg-warning/10 border border-warning/25 text-warning text-sm">
              ⚠️ Cette mission est déjà acceptée. La modifier remettra le prestataire en attente de re-confirmation.
            </div>
          )}

          <form onSubmit={handleEdit} className="space-y-4">
            <div>
              <label className="glass-label">Titre</label>
              <input name="title" defaultValue={mission.title} className="glass-input" required minLength={3} maxLength={120} />
            </div>
            <div>
              <label className="glass-label">Description</label>
              <textarea name="description" defaultValue={mission.description} className="glass-input min-h-[80px]" required minLength={10} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="glass-label">Type</label>
                <select name="type" defaultValue={mission.type} className="glass-input">
                  <option value="EVENT">Événement</option>
                  <option value="TRAINING">Formation</option>
                  <option value="OTHER">Autre</option>
                  {isCnpas && (
                    <>
                      <option value="ADS">🛡️ Agent de sécurité (ADS)</option>
                      <option value="SSIAP">🛡️ SSIAP</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="glass-label">Budget (€)</label>
                <input name="amount" type="number" defaultValue={mission.amount / 100} className="glass-input" required min={1} />
              </div>
            </div>

            {/* Profil recherché */}
            <div>
              <label className="glass-label">Types de prestataires requis</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
                {specialtyConfigs.filter((s) => {
                  if (!s.active) return false
                  if (!mission?.type) return true
                  if (s.category === 'BOTH') return true
                  if (mission.type === 'TRAINING') return s.category === 'TRAINING'
                  if (mission.type === 'EVENT') return s.category === 'EVENT'
                  return true
                }).map((s) => {
                  const checked = !!selectedSpecialties[s.specialty]
                  return (
                    <div
                      key={s.specialty}
                      className={[
                        'flex items-center gap-3 p-3 rounded-xl border transition-all',
                        checked ? 'bg-accent/10 border-accent/30' : 'border-white/[0.08] hover:border-white/[0.15]',
                      ].join(' ')}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSpecialty(s.specialty)}
                        className="accent-[#6E7FFF] w-4 h-4 flex-shrink-0 cursor-pointer"
                      />
                      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => toggleSpecialty(s.specialty)}>
                        <div className="flex items-center gap-1.5">
                          <span>{s.icon || '🔧'}</span>
                          <span className="text-xs font-medium text-text-primary">{s.shortLabel || s.label || s.specialty}</span>
                        </div>
                      </div>
                      {checked && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button type="button" onClick={() => setQuantity(s.specialty, (selectedSpecialties[s.specialty] ?? 1) - 1)}
                            className="w-6 h-6 rounded-lg bg-white/[0.08] hover:bg-white/[0.15] text-text-primary text-sm flex items-center justify-center">−</button>
                          <span className="text-sm font-semibold text-text-primary w-5 text-center">{selectedSpecialties[s.specialty]}</span>
                          <button type="button" onClick={() => setQuantity(s.specialty, (selectedSpecialties[s.specialty] ?? 1) + 1)}
                            className="w-6 h-6 rounded-lg bg-white/[0.08] hover:bg-white/[0.15] text-text-primary text-sm flex items-center justify-center">+</button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              {Object.keys(selectedSpecialties).length === 0 && (
                <p className="text-text-muted text-xs mt-2">Aucune spécialité — tous les prestataires seront notifiés.</p>
              )}
            </div>
            <AddressAutocomplete
              defaultAddress={mission.address}
              defaultCity={mission.city}
              defaultPostalCode={mission.postalCode}
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="glass-label">Date et heure</label>
                <DateTimePicker
                  name="date"
                  required
                  defaultValue={mission.date || undefined}
                  placeholder="Date de la mission…"
                />
              </div>
              <div>
                <label className="glass-label">Durée (minutes)</label>
                <input name="duration" type="number" defaultValue={mission.duration} className="glass-input" required min={15} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="glass-label">Rayon de recherche (km)</label>
                <input name="radiusKm" type="number" defaultValue={mission.radiusKm} className="glass-input" min={1} max={200} />
              </div>
              <div>
                <label className="glass-label">Candidature avant</label>
                <DateTimePicker
                  name="deadline"
                  defaultValue={mission.deadline || undefined}
                  placeholder="Aucune limite…"
                  align="right"
                />
              </div>
            </div>
            {mission.type === 'TRAINING' && (
              <div>
                <label className="glass-label">
                  Nombre minimum de participants{' '}
                  <span className="text-text-muted font-normal">(optionnel)</span>
                </label>
                <input
                  name="minParticipants"
                  type="number"
                  defaultValue={mission.minParticipants ?? ''}
                  placeholder="Ex : 10"
                  className="glass-input"
                  min={1}
                  max={10000}
                />
              </div>
            )}
            {editError && <p className="text-danger text-sm">{editError}</p>}
            <div className="flex gap-3">
              <button type="submit" disabled={editSaving} className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl">
                {editSaving ? 'Enregistrement…' : '💾 Enregistrer les modifications'}
              </button>
              <button type="button" onClick={() => setEditing(false)} className="glass-btn px-5 py-2.5 text-sm rounded-xl">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">

        {/* Description */}
        <div className="glass-card p-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Description</h2>
          <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-wrap">{mission.description}</p>
        </div>

        {/* Infos clés */}
        <div className="glass-card p-6">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">Détails</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <InfoItem icon="📅" label="Date" value={formatDate(mission.date)} />
            <InfoItem icon="⏱" label="Durée" value={formatDuration(mission.duration)} />
            <InfoItem icon="💰" label="Budget" value={formatEur(mission.amount)} />
            <InfoItem icon="📍" label="Lieu" value={`${mission.address}, ${mission.city} ${mission.postalCode}`} />
            <InfoItem icon="🔍" label="Rayon de recherche" value={`${mission.radiusKm} km`} />
            <InfoItem
              icon="📲"
              label="Push envoyés"
              value={
                mission.notifiedCount > 0
                  ? `${mission.notifiedCount} prestataire${mission.notifiedCount > 1 ? 's' : ''} notifié${mission.notifiedCount > 1 ? 's' : ''}`
                  : '0 — découvrable par les prestataires'
              }
            />
            {mission.deadline && (
              <InfoItem icon="⏰" label="Candidature avant" value={formatDate(mission.deadline)} />
            )}
            {mission.minParticipants != null && (
              <InfoItem icon="👥" label="Participants minimum" value={`${mission.minParticipants} personne${mission.minParticipants > 1 ? 's' : ''}`} />
            )}
            {mission.requiredSpecialties && mission.requiredSpecialties.length > 0 && (
              <div className="sm:col-span-2 lg:col-span-3 flex items-start gap-3">
                <span className="text-xl flex-shrink-0 mt-0.5">👤</span>
                <div>
                  <p className="text-text-muted text-xs mb-1.5">Profil recherché</p>
                  <div className="flex flex-wrap gap-2">
                    {mission.requiredSpecialties.map((s) => {
                      const cfg = specialtyConfigs.find((c) => c.specialty === s.specialty)
                      return (
                        <span key={s.specialty} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/[0.06] border border-white/[0.10] text-text-primary text-xs font-medium">
                          {cfg?.icon || '🔧'} {cfg?.shortLabel || cfg?.label || s.specialty}
                          <span className="text-text-muted">×</span>
                          <span className="text-accent font-semibold">{s.quantity}</span>
                        </span>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Paiement */}
        {mission.status === 'ASSIGNED' && (
          <div className="glass-card p-6">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">Paiement</h2>

            {actionError && (
              <div className="mb-4 px-3 py-2.5 rounded-xl bg-danger/10 border border-danger/25 text-danger text-xs">
                {actionError}
              </div>
            )}

            {mission.payment ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${PAYMENT_STATUS[mission.payment.status]?.color}`}>
                      {PAYMENT_STATUS[mission.payment.status]?.label}
                    </span>
                    <span className="text-text-secondary text-xs">{formatEur(mission.payment.amount)}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-text-muted text-xs">Commission Velink : {formatEur(mission.payment.platformFee)}</p>
                    <p className="text-success text-xs">Prestataire reçoit : {formatEur(mission.payment.providerNet)}</p>
                  </div>
                </div>

                {canCompleteWithPayment && (
                  <button
                    onClick={handleCompleteWithPayment}
                    disabled={completeLoading}
                    className="glass-btn-accent w-full py-3 text-sm rounded-xl"
                  >
                    {completeLoading ? 'Finalisation…' : '✓ Confirmer la fin de mission — libérer le paiement'}
                  </button>
                )}

                {mission.payment.status === 'CAPTURED' && (
                  <div className="flex items-center gap-2 text-success text-sm">
                    <span>✅</span> Paiement libéré au prestataire
                    {mission.payment.capturedAt && (
                      <span className="text-text-muted text-xs ml-1">
                        le {new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' }).format(new Date(mission.payment.capturedAt))}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (canPayStripe || canPayPayPal) ? (
              <div className="space-y-3">
                <p className="text-text-secondary text-sm">
                  Les fonds sont retenus jusqu'à la fin de la mission, puis libérés au prestataire.
                </p>
                {/* Acceptation CGV */}
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={cgvAccepted}
                    onChange={(e) => setCgvAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-[#6E7FFF] shrink-0"
                  />
                  <span className="text-text-secondary text-sm group-hover:text-text-primary transition-colors">
                    J'ai lu et j'accepte les{' '}
                    <a href="/cgv" target="_blank" className="text-accent hover:underline">
                      Conditions Générales de Vente
                    </a>
                  </span>
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  {canPayStripe && (
                    <button
                      onClick={handlePay}
                      disabled={paymentLoading || paypalLoading || !cgvAccepted}
                      className="glass-btn-accent flex-1 py-3 text-sm rounded-xl disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {paymentLoading ? 'Redirection…' : `💳 Payer via Stripe`}
                    </button>
                  )}
                  {canPayPayPal && (
                    <button
                      onClick={handlePayPal}
                      disabled={paymentLoading || paypalLoading || !cgvAccepted}
                      className="flex-1 py-3 text-sm rounded-xl font-medium border transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ background: 'rgba(255,196,57,0.15)', borderColor: 'rgba(255,196,57,0.35)', color: '#FFB700' }}
                    >
                      {paypalLoading ? 'Redirection…' : '🅿 Payer via PayPal'}
                    </button>
                  )}
                </div>
                {!canPayStripe && (
                  <p className="text-text-muted text-xs">
                    Stripe indisponible — le prestataire n'a pas encore activé son compte Stripe.
                  </p>
                )}
                {!canPayPayPal && (
                  <p className="text-text-muted text-xs">
                    PayPal indisponible — le prestataire n'a pas renseigné son email PayPal.
                  </p>
                )}
              </div>
            ) : (
              <div className="p-4 rounded-xl bg-white/[0.04] border border-white/[0.08] text-text-secondary text-sm">
                ⚠️ Le prestataire doit configurer Stripe ou renseigner son email PayPal dans son profil Velink.
              </div>
            )}
          </div>
        )}

        {/* Terminer sans paiement */}
        {canCompleteNoPayment && (
          <div className="glass-card p-6">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Clôturer la mission</h2>
            {actionError && (
              <div className="mb-4 px-3 py-2.5 rounded-xl bg-danger/10 border border-danger/25 text-danger text-xs">
                {actionError}
              </div>
            )}
            <p className="text-text-secondary text-sm mb-4">
              La mission a été réalisée ? Marquez-la comme terminée pour débloquer l'évaluation du prestataire.
            </p>
            <button
              onClick={handleCompleteNoPayment}
              disabled={completeLoading}
              className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl"
            >
              {completeLoading ? 'Finalisation…' : '✓ Terminer la mission'}
            </button>
          </div>
        )}

        {/* Évaluation */}
        {mission.status === 'COMPLETED' && (
          <div className="glass-card p-6">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-5">
              Évaluation du prestataire
            </h2>

            {mission.review ? (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-text-muted text-xs mb-2">Ponctualité</p>
                    <div className="flex items-center gap-2">
                      <StarRating value={mission.review.punctuality} readonly />
                      <span className="text-warning text-sm font-semibold">{mission.review.punctuality}/5</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-text-muted text-xs mb-2">Comportement</p>
                    <div className="flex items-center gap-2">
                      <StarRating value={mission.review.behavior} readonly />
                      <span className="text-warning text-sm font-semibold">{mission.review.behavior}/5</span>
                    </div>
                  </div>
                </div>
                <p className="text-text-muted text-xs">
                  Note globale : <span className="text-warning font-semibold">{mission.review.rating}/5</span>
                  <span className="ml-2">— le {formatDate(mission.review.createdAt)}</span>
                </p>
                {mission.review.comment && (
                  <p className="text-text-secondary text-sm leading-relaxed italic">
                    &ldquo;{mission.review.comment}&rdquo;
                  </p>
                )}
                {mission.review.clientFeedback && (
                  <div className="p-3 rounded-xl bg-white/[0.04] border border-white/[0.08]">
                    <p className="text-text-muted text-xs mb-1">Retour client</p>
                    <p className="text-text-secondary text-sm leading-relaxed">{mission.review.clientFeedback}</p>
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleReview} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <p className="text-text-secondary text-xs mb-2">Ponctualité <span className="text-danger">*</span></p>
                    <StarRating value={reviewPunctuality} onChange={setReviewPunctuality} />
                  </div>
                  <div>
                    <p className="text-text-secondary text-xs mb-2">Comportement <span className="text-danger">*</span></p>
                    <StarRating value={reviewBehavior} onChange={setReviewBehavior} />
                  </div>
                </div>
                <div>
                  <p className="text-text-secondary text-xs mb-2">Commentaire général (facultatif)</p>
                  <textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Votre avis général sur la mission…"
                    rows={2}
                    maxLength={1000}
                    className="w-full bg-white/[0.05] border border-white/[0.12] rounded-xl px-4 py-3 text-text-primary text-sm placeholder:text-text-muted resize-none focus:outline-none focus:border-accent/50"
                  />
                </div>
                <div>
                  <p className="text-text-secondary text-xs mb-2">Retour client (facultatif)</p>
                  <textarea
                    value={reviewClientFeedback}
                    onChange={(e) => setReviewClientFeedback(e.target.value)}
                    placeholder="Retour de l'entreprise cliente sur la prestation…"
                    rows={3}
                    maxLength={2000}
                    className="w-full bg-white/[0.05] border border-white/[0.12] rounded-xl px-4 py-3 text-text-primary text-sm placeholder:text-text-muted resize-none focus:outline-none focus:border-accent/50"
                  />
                </div>
                {reviewError && (
                  <p className="text-danger text-xs">{reviewError}</p>
                )}
                <button
                  type="submit"
                  disabled={reviewLoading || reviewPunctuality === 0 || reviewBehavior === 0}
                  className="glass-btn-accent px-6 py-2.5 text-sm rounded-xl disabled:opacity-50"
                >
                  {reviewLoading ? 'Envoi…' : 'Envoyer l\'évaluation'}
                </button>
              </form>
            )}
          </div>
        )}

        {/* Candidatures */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider">
              Candidatures
            </h2>
            <span className="text-xs text-text-muted">
              {mission._count.assignments} au total
            </span>
          </div>

          {mission.assignments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-3xl mb-3">📲</p>
              <p className="text-text-secondary text-sm">
                {mission.status === 'OPEN'
                  ? mission.notifiedCount > 0
                    ? `${mission.notifiedCount} prestataire${mission.notifiedCount > 1 ? 's' : ''} notifié${mission.notifiedCount > 1 ? 's' : ''} par push — en attente de candidatures.`
                    : 'La mission est visible par les prestataires à proximité — en attente de candidatures.'
                  : 'Aucune candidature reçue.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {acceptedAssignments.length > 0 && (
                <>
                  <p className="text-xs text-success font-medium">Prestataire assigné</p>
                  {acceptedAssignments.map((a) => <ProviderCard key={a.id} assignment={a} />)}
                </>
              )}
              {pendingAssignments.length > 0 && (
                <>
                  <p className="text-xs text-warning font-medium mt-4">En attente de réponse</p>
                  {pendingAssignments.map((a) => <ProviderCard key={a.id} assignment={a} />)}
                </>
              )}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}
